/**
 * The OpenAI Realtime voice, over WebRTC in the window (RFC 0307 3a, S1).
 *
 * The mic goes straight to OpenAI and the reply comes straight back: nothing
 * passes through the daemon or the relay. The call is opened with the client
 * secret the daemon fetched, by posting the SDP offer to `/v1/realtime/calls`.
 * Events run on the `oai-events` data channel and are read by
 * `readRealtimeEvent`.
 *
 * `notify` adds context as a system item. Only a notice marked `speak` asks
 * for a reply, and only when the line is quiet: nobody speaking, no reply in
 * progress. Otherwise the reply waits for the next pause, so the voice never
 * talks over the person.
 */

import { micLevel, openLine } from './browserCall'
import { callLanguage } from './language'
import { readRealtimeEvent, realtimeClient } from './realtimeEvents'
import { ReplyGate } from './replyGate'
import type { VoiceCall, VoiceConnectOptions, VoiceProvider } from './types'

export const OPENAI_REALTIME_CALLS_URL =
  'https://api.openai.com/v1/realtime/calls'

export function openaiRealtimeProvider(): VoiceProvider {
  return { connect: connectOpenAIRealtime }
}

async function connectOpenAIRealtime(
  opts: VoiceConnectOptions,
): Promise<VoiceCall> {
  const line = openLine(opts)
  const { signal } = opts
  const mic =
    opts.microphone ??
    (await navigator.mediaDevices.getUserMedia({ audio: true }))
  line.onTeardown(() => {
    for (const t of mic.getTracks()) t.stop()
  })
  // Hung up while the mic was being granted: it is let go above, and the line
  // is not claimed, which would end the call that replaced this one.
  line.throwIfHungUp()
  const pc = new RTCPeerConnection()
  const audio = new Audio()
  audio.autoplay = true
  pc.ontrack = (e) => {
    audio.srcObject = e.streams[0] ?? null
  }
  for (const track of mic.getAudioTracks()) pc.addTrack(track, mic)
  const dc = pc.createDataChannel('oai-events')
  line.onTeardown(() => {
    dc.close()
    pc.close()
    audio.srcObject = null
  })
  line.claim()

  const answered = new Set<string>()
  const send = (event: unknown) => {
    if (dc.readyState === 'open') dc.send(JSON.stringify(event))
  }
  const gate = new ReplyGate((reason) => {
    // How many replies one thing the person said turned into. The provider's
    // own reply to their speech is not counted; more than one of these per
    // line is the pile-up to look for.
    console.debug(
      `[voice] response.create #${gate.askedSincePerson} since you spoke (${reason})`,
    )
    send(realtimeClient.respond())
  })

  dc.onopen = () => {
    send(
      realtimeClient.sessionUpdate({
        instructions: opts.instructions,
        tools: opts.tools,
        language: callLanguage(),
        ...(opts.respondToSpeech === false ? { respondToSpeech: false } : {}),
      }),
    )
  }
  dc.onmessage = (m) => {
    let event: unknown
    try {
      event = JSON.parse(String(m.data))
    } catch {
      return
    }
    for (const read of readRealtimeEvent(event)) {
      switch (read.kind) {
        case 'transcript':
          if (read.speaker === 'person') gate.personSaidSomething()
          opts.onTranscript({ speaker: read.speaker, text: read.text })
          break
        case 'speech_started':
          gate.speechStarted()
          break
        case 'speech_stopped':
          gate.speechStopped()
          opts.onSpeechEnded?.()
          break
        case 'response_started':
          gate.replyStarted()
          break
        case 'response_done':
          gate.replyDone()
          break
        case 'tool_call':
          if (answered.has(read.callId)) break
          answered.add(read.callId)
          gate.toolStarted()
          void opts
            .onToolCall(read.name, read.args)
            .catch((err: unknown) => ({
              error: err instanceof Error ? err.message : String(err),
            }))
            .then((output) => {
              send(realtimeClient.toolResult(read.callId, output ?? {}))
              // One reply for the whole batch, once its last result is in.
              gate.toolAnswered()
            })
          break
        case 'error':
          console.warn('[voice] realtime error:', read.message)
          break
      }
    }
  }
  pc.onconnectionstatechange = () => {
    if (pc.connectionState === 'failed' || pc.connectionState === 'closed')
      line.end({ error: 'the voice call dropped' })
  }

  try {
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    let answer: string
    if (opts.exchangeSdp) {
      // Billed: the proxy opens the call and meters it (RFC 0345 S8).
      answer = await opts.exchangeSdp(offer.sdp ?? '')
    } else {
      const res = await fetch(OPENAI_REALTIME_CALLS_URL, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${opts.clientSecret}`,
          'Content-Type': 'application/sdp',
        },
        ...(signal ? { signal } : {}),
      })
      if (!res.ok)
        throw new Error(`the voice could not connect (${res.status})`)
      answer = await res.text()
    }
    await pc.setRemoteDescription({ type: 'answer', sdp: answer })
    line.level.start(micLevel(line, mic))
    // Live only once events can flow. A notice sent before the channel opens
    // is dropped, and its reply request with it, which left the reply gate
    // waiting on a response that was never asked for.
    await channelOpen(dc, signal)
  } catch (err) {
    // Offline, refused, or a bad answer: release the mic and the connection
    // before saying so, or the mic light stays on with no call behind it.
    line.end()
    if (signal?.aborted) line.throwIfHungUp()
    throw err instanceof Error ? err : new Error(String(err))
  }
  line.live()

  return {
    notify(text, notifyOpts) {
      send(realtimeClient.context(text))
      gate.noticed(notifyOpts?.speak === true)
    },
    setMicMuted(muted) {
      line.level.setMuted(muted)
      for (const t of mic.getAudioTracks()) t.enabled = !muted
    },
    setSpeakerMuted(muted) {
      audio.muted = muted
    },
    onLevel: line.level.onLevel,
    hangUp() {
      line.end()
    },
  }
}

/** How long the event channel may take to open once the call is answered. */
const CHANNEL_OPEN_MS = 10_000

function channelOpen(dc: RTCDataChannel, signal?: AbortSignal): Promise<void> {
  if (dc.readyState === 'open') return Promise.resolve()
  return new Promise((resolve, reject) => {
    const done = (err?: Error) => {
      clearTimeout(timer)
      dc.removeEventListener('open', opened)
      dc.removeEventListener('close', closed)
      signal?.removeEventListener('abort', closed)
      if (err) reject(err)
      else resolve()
    }
    const opened = () => done()
    const closed = () => done(new Error('the voice could not connect'))
    const timer = setTimeout(closed, CHANNEL_OPEN_MS)
    dc.addEventListener('open', opened)
    dc.addEventListener('close', closed)
    signal?.addEventListener('abort', closed)
  })
}
